/**
 * 🎯 usePumperWorkload Hook
 * 
 * Task #78: The smartest workload hook ever built
 * 
 * Provides comprehensive workload analytics for pumpers:
 * - Individual workload calculation with full breakdown
 * - Team-wide comparison and distribution
 * - Intelligent recommendations for rebalancing
 * - Quick checks for assignment dialogs
 * - Historical trend analysis (future)
 * 
 * Formula Reference (WELL_ASSIGNMENT_FEATURE.md):
 *   wellCountScore   = min(wellCount × 2, 40)
 *   distanceScore    = min(dailyMiles ÷ 10, 25)
 *   frequencyScore   = min(avgCheckFrequency × 5, 20)
 *   priorityScore    = min(highPriorityWellCount × 3, 15)
 *   TOTAL = min(sum, 100)
 * 
 * Thresholds:
 *   light:      0-29  (🟢 can take more wells)
 *   balanced:  30-60  (🟡 optimal)
 *   heavy:     61-80  (🟠 nearing capacity)
 *   overloaded: 81-100 (🔴 needs redistribution)
 * 
 * @module usePumperWorkload
 * @version 1.0.0
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { getCompanyStorageKey } from '../utils/tenantContext';

// Service imports
import {
  calculateWorkloadScore as calculateWorkloadScoreService,
  getWorkloadStatus,
  getWorkloadRecommendations,
  getTeamWorkloadComparison,
  getTeamWorkloadSummary,
  quickWorkloadCheck,
  // getWorkloadTrend, // Reserved for future trend analysis
} from '../utils/services/wellAssignmentService';

// Type imports
import type {
  PumperWorkload,
  WorkloadStatus,
  // WorkloadSuggestedChange, // Reserved for future use
} from '../types/wellAssignment';

import {
  WORKLOAD_THRESHOLDS,
  WORKLOAD_STATUS_LABELS,
  WORKLOAD_STATUS_EMOJI_LABELS,
  WORKLOAD_STATUS_DESCRIPTIONS,
  WORKLOAD_STATUS_COLORS,
  WORKLOAD_STATUS_RECOMMENDATIONS,
  WORKLOAD_SCORE_WEIGHTS,
  getWorkloadStatusFromScore,
  getWorkloadScoreBreakdown,
  createEmptyWorkload,
} from '../types/wellAssignment';

// ═══════════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Score breakdown for visualization
 */
export interface WorkloadScoreBreakdown {
  wellCountScore: number;
  distanceScore: number;
  frequencyScore: number;
  priorityScore: number;
  rawTotal: number;
  cappedTotal: number;
}

/**
 * Individual well's contribution to workload
 */
export interface WellContribution {
  id: string;
  name: string;
  contribution: string;
  points: number;
}

/**
 * Full workload calculation result
 */
export interface WorkloadCalculation {
  workload: PumperWorkload;
  breakdown: WorkloadScoreBreakdown;
  inputs: {
    wellCount: number;
    activeWellCount: number;
    temporaryCoverageCount: number;
    totalDailyMiles: number;
    avgCheckFrequency: number;
    highPriorityWellCount: number;
  };
  wells: WellContribution[];
}

/**
 * Workload recommendation
 */
export interface WorkloadRecommendation {
  priority: 'high' | 'medium' | 'low';
  action: 'add_wells' | 'remove_wells' | 'rebalance';
  description: string;
  impact: string;
  wellCount?: number;
  targetPumper?: { id: string; name: string };
}

/**
 * Enhanced pumper with full workload context
 */
export interface PumperWithWorkload {
  id: string;
  name: string;
  email?: string;
  role: string;
  workload: PumperWorkload;
  breakdown: WorkloadScoreBreakdown;
  // UI-ready properties
  statusLabel: string;
  statusEmoji: string;
  statusColor: {
    bg: string;
    text: string;
    border: string;
    dot: string;
  };
  canAssignMore: boolean;
  capacityRemaining: number;
  assignmentWarning: string | null;
  recommendation: string;
  // Ranking
  rank: number;
  percentile: number;
}

/**
 * Team workload comparison
 */
export interface TeamWorkloadComparison {
  pumperId: string;
  pumperName: string;
  wellCount: number;
  workloadScore: number;
  workloadStatus: WorkloadStatus;
  canTakeMore: boolean;
  capacityRemaining: number;
}

/**
 * Team summary for dashboard
 */
export interface TeamWorkloadSummary {
  totalPumpers: number;
  totalWells: number;
  avgWorkloadScore: number;
  distribution: {
    light: number;
    balanced: number;
    heavy: number;
    overloaded: number;
  };
  mostOverloaded: TeamWorkloadComparison | null;
  mostAvailable: TeamWorkloadComparison | null;
  recommendations: string[];
  // Additional analytics
  healthScore: number;        // 0-100 team health
  balanceIndex: number;       // Standard deviation of scores
  atRiskPumpers: string[];    // IDs of overloaded/heavy pumpers
}

/**
 * Hook options
 */
export interface UsePumperWorkloadOptions {
  /** Current user for role checks */
  user: {
    id: string;
    role: string;
  } | null;
  /** Specific pumper to focus on (or null for team view) */
  pumperId?: string;
  /** Pumper name (required if pumperId provided) */
  pumperName?: string;
  /** Auto-refresh interval in ms (0 = disabled) */
  refreshInterval?: number;
  /** Include full team comparison data */
  includeTeamComparison?: boolean;
}

/**
 * Hook state
 */
export interface UsePumperWorkloadState {
  // Individual pumper data
  workload: PumperWorkload | null;
  breakdown: WorkloadScoreBreakdown | null;
  recommendations: WorkloadRecommendation[];
  
  // Team data
  teamComparison: TeamWorkloadComparison[];
  teamSummary: TeamWorkloadSummary | null;
  
  // UI State
  loading: boolean;
  error: string | null;
  lastRefresh: Date | null;
}

/**
 * Hook actions
 */
export interface UsePumperWorkloadActions {
  // Queries
  refresh: () => Promise<void>;
  calculateWorkload: (pumperId: string, pumperName: string) => Promise<WorkloadCalculation | null>;
  getStatus: (pumperId: string, pumperName: string) => Promise<{
    status: WorkloadStatus;
    score: number;
    label: string;
    emoji: string;
    description: string;
    canAssign: boolean;
    warning: string | null;
    suggestion: string;
  } | null>;
  getRecommendations: (pumperId: string, pumperName: string) => Promise<WorkloadRecommendation[]>;
  
  // Quick checks for assignment dialogs
  quickCheck: (pumperId: string) => Promise<{
    canAssign: boolean;
    status: WorkloadStatus;
    score: number;
    warning: string | null;
  }>;
  
  // Team operations
  refreshTeam: () => Promise<void>;
  findBestPumperForAssignment: () => PumperWithWorkload | null;
  findPumpersNeedingRelief: () => PumperWithWorkload[];
  suggestRebalancing: () => RebalancingSuggestion[];
  
  // Selectors
  getPumperWorkload: (pumperId: string) => PumperWithWorkload | undefined;
  getPumpersByStatus: (status: WorkloadStatus) => PumperWithWorkload[];
  getRankedPumpers: (order: 'lightest' | 'heaviest') => PumperWithWorkload[];
  
  // Utilities
  formatScore: (score: number) => string;
  getStatusColor: (status: WorkloadStatus) => typeof WORKLOAD_STATUS_COLORS[WorkloadStatus];
  getThresholds: () => typeof WORKLOAD_THRESHOLDS;
  getWeights: () => typeof WORKLOAD_SCORE_WEIGHTS;
}

/**
 * Rebalancing suggestion
 */
export interface RebalancingSuggestion {
  from: { id: string; name: string; currentScore: number };
  to: { id: string; name: string; currentScore: number };
  wellsToTransfer: number;
  projectedFromScore: number;
  projectedToScore: number;
  impact: string;
  priority: 'critical' | 'recommended' | 'optional';
}

/**
 * Full hook return type
 */
export type UsePumperWorkloadReturn = UsePumperWorkloadState & UsePumperWorkloadActions;

// ═══════════════════════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Calculate health score for team (0-100)
 * 100 = all pumpers balanced, 0 = everyone overloaded
 */
function calculateTeamHealthScore(distribution: TeamWorkloadSummary['distribution']): number {
  const total = distribution.light + distribution.balanced + distribution.heavy + distribution.overloaded;
  if (total === 0) return 100;
  
  // Weight: light=100, balanced=100, heavy=50, overloaded=0
  const score = (
    (distribution.light * 100) +
    (distribution.balanced * 100) +
    (distribution.heavy * 50) +
    (distribution.overloaded * 0)
  ) / total;
  
  return Math.round(score);
}

/**
 * Calculate balance index (lower = more balanced)
 * Uses standard deviation of workload scores
 */
function calculateBalanceIndex(comparisons: TeamWorkloadComparison[]): number {
  if (comparisons.length < 2) return 0;
  
  const scores = comparisons.map(c => c.workloadScore);
  const mean = scores.reduce((a, b) => a + b, 0) / scores.length;
  const variance = scores.reduce((sum, s) => sum + Math.pow(s - mean, 2), 0) / scores.length;
  
  return Math.round(Math.sqrt(variance));
}

/**
 * Transform team comparison to full PumperWithWorkload
 */
function enrichPumperData(
  comparison: TeamWorkloadComparison,
  allComparisons: TeamWorkloadComparison[],
  index: number
): PumperWithWorkload {
  const status = comparison.workloadStatus;
  const colorConfig = WORKLOAD_STATUS_COLORS[status];
  const recommendation = WORKLOAD_STATUS_RECOMMENDATIONS[status];
  
  // Calculate percentile (higher score = higher percentile)
  const sortedScores = [...allComparisons].sort((a, b) => a.workloadScore - b.workloadScore);
  const percentileIndex = sortedScores.findIndex(c => c.pumperId === comparison.pumperId);
  const percentile = Math.round((percentileIndex / allComparisons.length) * 100);
  
  return {
    id: comparison.pumperId,
    name: comparison.pumperName,
    role: 'pumper',
    workload: {
      pumperId: comparison.pumperId,
      pumperName: comparison.pumperName,
      wellCount: comparison.wellCount,
      activeWellCount: comparison.wellCount, // Approximate
      temporaryCoverageCount: 0, // Would need separate query
      totalDailyMiles: 0, // Would need separate query
      avgCheckFrequency: 0, // Would need separate query
      highPriorityWellCount: 0, // Would need separate query
      workloadScore: comparison.workloadScore,
      workloadStatus: status,
    },
    breakdown: {
      wellCountScore: Math.min(comparison.wellCount * 2, 40),
      distanceScore: 0, // Simplified
      frequencyScore: 0,
      priorityScore: 0,
      rawTotal: comparison.workloadScore,
      cappedTotal: comparison.workloadScore,
    },
    statusLabel: WORKLOAD_STATUS_LABELS[status],
    statusEmoji: WORKLOAD_STATUS_EMOJI_LABELS[status].split(' ')[0], // Just emoji
    statusColor: colorConfig,
    canAssignMore: comparison.canTakeMore,
    capacityRemaining: comparison.capacityRemaining,
    assignmentWarning: recommendation.warning,
    recommendation: recommendation.suggestion,
    rank: index + 1,
    percentile,
  };
}

/**
 * Parse contribution string to extract points
 * E.g., "+2 (well count), +3 (high priority)" -> 5
 */
function parseContributionPoints(contribution: string): number {
  const matches = contribution.match(/\+(\d+)/g);
  if (!matches) return 2; // Default well count contribution
  return matches.reduce((sum, m) => sum + parseInt(m.replace('+', ''), 10), 0);
}

/**
 * Cache key for localStorage
 */
const CACHE_KEY = 'pumperWorkload';
const TEAM_CACHE_KEY = 'teamWorkload';
const CACHE_TTL = 2 * 60 * 1000; // 2 minutes (workload changes frequently)

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN HOOK
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * usePumperWorkload - The smartest workload hook ever
 * 
 * @param options - Hook configuration
 * @returns State and actions for workload management
 * 
 * @example
 * ```tsx
 * // Individual pumper view
 * const { workload, breakdown, recommendations } = usePumperWorkload({
 *   user,
 *   pumperId: 'pumper123',
 *   pumperName: 'John Smith',
 * });
 * 
 * // Team dashboard view
 * const {
 *   teamSummary,
 *   teamComparison,
 *   findBestPumperForAssignment,
 *   suggestRebalancing,
 * } = usePumperWorkload({ user, includeTeamComparison: true });
 * ```
 */
export function usePumperWorkload(
  options: UsePumperWorkloadOptions
): UsePumperWorkloadReturn {
  const {
    user,
    pumperId,
    pumperName,
    refreshInterval = 0,
    includeTeamComparison = false,
  } = options;
  
  // ─────────────────────────────────────────────────────────────────────────────
  // STATE
  // ─────────────────────────────────────────────────────────────────────────────
  
  const [workload, setWorkload] = useState<PumperWorkload | null>(null);
  const [breakdown, setBreakdown] = useState<WorkloadScoreBreakdown | null>(null);
  const [recommendations, setRecommendations] = useState<WorkloadRecommendation[]>([]);
  const [teamComparison, setTeamComparison] = useState<TeamWorkloadComparison[]>([]);
  const [teamSummary, setTeamSummary] = useState<TeamWorkloadSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  
  // Refs for cleanup
  const mountedRef = useRef(true);
  const refreshTimerRef = useRef<NodeJS.Timeout | null>(null);
  const loadingRef = useRef(false);
  
  // Role checks
  const isManager = user?.role === 'admin' || user?.role === 'manager' || user?.role === 'superadmin';
  
  // ─────────────────────────────────────────────────────────────────────────────
  // DERIVED STATE
  // ─────────────────────────────────────────────────────────────────────────────
  
  const enrichedTeam = useMemo((): PumperWithWorkload[] => {
    return teamComparison.map((c, i) => enrichPumperData(c, teamComparison, i));
  }, [teamComparison]);
  
  // ─────────────────────────────────────────────────────────────────────────────
  // CACHING
  // ─────────────────────────────────────────────────────────────────────────────
  
  const getCachedWorkload = useCallback((targetPumperId: string): PumperWorkload | null => {
    try {
      const key = getCompanyStorageKey(`${CACHE_KEY}:${targetPumperId}`);
      const cached = localStorage.getItem(key);
      if (!cached) return null;
      
      const { data, timestamp } = JSON.parse(cached);
      if (Date.now() - timestamp > CACHE_TTL) {
        localStorage.removeItem(key);
        return null;
      }
      return data;
    } catch {
      return null;
    }
  }, []);
  
  const setCachedWorkload = useCallback((targetPumperId: string, data: PumperWorkload) => {
    try {
      const key = getCompanyStorageKey(`${CACHE_KEY}:${targetPumperId}`);
      localStorage.setItem(key, JSON.stringify({ data, timestamp: Date.now() }));
    } catch {
      // Ignore
    }
  }, []);
  
  const getCachedTeam = useCallback((): TeamWorkloadComparison[] | null => {
    try {
      const key = getCompanyStorageKey(TEAM_CACHE_KEY);
      const cached = localStorage.getItem(key);
      if (!cached) return null;
      
      const { data, timestamp } = JSON.parse(cached);
      if (Date.now() - timestamp > CACHE_TTL) {
        localStorage.removeItem(key);
        return null;
      }
      return data;
    } catch {
      return null;
    }
  }, []);
  
  const setCachedTeam = useCallback((data: TeamWorkloadComparison[]) => {
    try {
      const key = getCompanyStorageKey(TEAM_CACHE_KEY);
      localStorage.setItem(key, JSON.stringify({ data, timestamp: Date.now() }));
    } catch {
      // Ignore
    }
  }, []);
  
  // ─────────────────────────────────────────────────────────────────────────────
  // DATA LOADING
  // ─────────────────────────────────────────────────────────────────────────────
  
  const loadIndividualWorkload = useCallback(async (
    targetPumperId: string,
    targetPumperName: string
  ) => {
    try {
      const result = await calculateWorkloadScoreService(targetPumperId, targetPumperName);
      
      if (mountedRef.current) {
        setWorkload(result.workload);
        setBreakdown(result.breakdown);
        setCachedWorkload(targetPumperId, result.workload);
      }
      
      // Get recommendations
      const recsResult = await getWorkloadRecommendations(targetPumperId, targetPumperName);
      if (mountedRef.current) {
        setRecommendations(recsResult.recommendations);
      }
      
      return result;
    } catch (err) {
      console.error('Failed to load individual workload:', err);
      
      // Try cache
      const cached = getCachedWorkload(targetPumperId);
      if (cached && mountedRef.current) {
        setWorkload(cached);
        const cachedBreakdown = getWorkloadScoreBreakdown(cached);
        setBreakdown({
          ...cachedBreakdown,
          rawTotal: cachedBreakdown.total,
          cappedTotal: cachedBreakdown.total,
        });
      }
      throw err;
    }
  }, [getCachedWorkload, setCachedWorkload]);
  
  const loadTeamWorkload = useCallback(async () => {
    try {
      const [comparison, summary] = await Promise.all([
        getTeamWorkloadComparison(),
        getTeamWorkloadSummary(),
      ]);
      
      if (mountedRef.current) {
        setTeamComparison(comparison);
        
        // Enhance summary with calculated metrics
        const enhancedSummary: TeamWorkloadSummary = {
          ...summary,
          healthScore: calculateTeamHealthScore(summary.distribution),
          balanceIndex: calculateBalanceIndex(comparison),
          atRiskPumpers: comparison
            .filter(c => c.workloadStatus === 'overloaded' || c.workloadStatus === 'heavy')
            .map(c => c.pumperId),
        };
        
        setTeamSummary(enhancedSummary);
        setCachedTeam(comparison);
      }
    } catch (err) {
      console.error('Failed to load team workload:', err);
      
      // Try cache
      const cached = getCachedTeam();
      if (cached && mountedRef.current) {
        setTeamComparison(cached);
      }
      throw err;
    }
  }, [getCachedTeam, setCachedTeam]);
  
  const loadData = useCallback(async (showLoading = true) => {
    if (loadingRef.current) return;
    
    // Handle null user - set empty state and stop loading
    if (!user) {
      if (mountedRef.current) {
        setWorkload(null);
        setBreakdown(null);
        setRecommendations([]);
        setTeamComparison([]);
        setTeamSummary(null);
        setLoading(false);
      }
      return;
    }
    
    loadingRef.current = true;
    if (showLoading && mountedRef.current) {
      setLoading(true);
    }
    setError(null);
    
    try {
      // Load individual workload if pumper specified
      if (pumperId && pumperName) {
        await loadIndividualWorkload(pumperId, pumperName);
      } else if (user.role === 'pumper') {
        // Load current user's workload
        await loadIndividualWorkload(user.id, user.role);
      }
      
      // Load team comparison if requested or user is manager
      if (includeTeamComparison || isManager) {
        await loadTeamWorkload();
      }
      
      if (mountedRef.current) {
        setLastRefresh(new Date());
      }
    } catch (err: unknown) {
      if (mountedRef.current) {
        setError(err instanceof Error ? err.message : 'Failed to load workload data');
      }
    } finally {
      loadingRef.current = false;
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.role, pumperId, pumperName, includeTeamComparison, isManager, loadIndividualWorkload, loadTeamWorkload]);
  
  // ─────────────────────────────────────────────────────────────────────────────
  // EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  
  useEffect(() => {
    mountedRef.current = true;
    loadData();
    
    return () => {
      mountedRef.current = false;
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
      }
    };
  }, [loadData]);
  
  useEffect(() => {
    if (refreshInterval > 0 && refreshTimerRef.current === null) {
      refreshTimerRef.current = setInterval(() => {
        loadData(false);
      }, refreshInterval);
    }
    
    return () => {
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, [refreshInterval, loadData]);
  
  // ─────────────────────────────────────────────────────────────────────────────
  // ACTIONS
  // ─────────────────────────────────────────────────────────────────────────────
  
  const refresh = useCallback(async () => {
    await loadData(true);
  }, [loadData]);
  
  const refreshTeam = useCallback(async () => {
    setLoading(true);
    try {
      await loadTeamWorkload();
      setLastRefresh(new Date());
    } catch {
      // Error already set in loadTeamWorkload
    } finally {
      setLoading(false);
    }
  }, [loadTeamWorkload]);
  
  const calculateWorkload = useCallback(async (
    targetPumperId: string,
    targetPumperName: string
  ): Promise<WorkloadCalculation | null> => {
    try {
      const result = await calculateWorkloadScoreService(targetPumperId, targetPumperName);
      // Transform to our WorkloadCalculation type
      return {
        workload: result.workload,
        breakdown: result.breakdown,
        inputs: result.inputs,
        wells: result.wells.map(w => ({
          ...w,
          points: parseContributionPoints(w.contribution),
        })),
      };
    } catch {
      return null;
    }
  }, []);
  
  const getStatus = useCallback(async (
    targetPumperId: string,
    targetPumperName: string
  ) => {
    try {
      return await getWorkloadStatus(targetPumperId, targetPumperName);
    } catch {
      return null;
    }
  }, []);
  
  const getRecommendationsAction = useCallback(async (
    targetPumperId: string,
    targetPumperName: string
  ): Promise<WorkloadRecommendation[]> => {
    try {
      const result = await getWorkloadRecommendations(targetPumperId, targetPumperName);
      return result.recommendations;
    } catch {
      return [];
    }
  }, []);
  
  const quickCheck = useCallback(async (targetPumperId: string) => {
    return quickWorkloadCheck(targetPumperId);
  }, []);
  
  // ─────────────────────────────────────────────────────────────────────────────
  // TEAM OPERATIONS
  // ─────────────────────────────────────────────────────────────────────────────
  
  const findBestPumperForAssignment = useCallback((): PumperWithWorkload | null => {
    const available = enrichedTeam
      .filter(p => p.canAssignMore)
      .sort((a, b) => a.workload.workloadScore - b.workload.workloadScore);
    
    return available[0] || null;
  }, [enrichedTeam]);
  
  const findPumpersNeedingRelief = useCallback((): PumperWithWorkload[] => {
    return enrichedTeam
      .filter(p => p.workload.workloadStatus === 'overloaded' || p.workload.workloadStatus === 'heavy')
      .sort((a, b) => b.workload.workloadScore - a.workload.workloadScore);
  }, [enrichedTeam]);
  
  const suggestRebalancing = useCallback((): RebalancingSuggestion[] => {
    const overloaded = enrichedTeam.filter(p => p.workload.workloadStatus === 'overloaded');
    const available = enrichedTeam.filter(p => p.canAssignMore && p.capacityRemaining > 0);
    
    const suggestions: RebalancingSuggestion[] = [];
    
    for (const from of overloaded) {
      for (const to of available) {
        // Calculate how many wells to transfer
        const wellsToTransfer = Math.min(
          Math.ceil(from.workload.wellCount * 0.2), // 20% of overloaded
          to.capacityRemaining,
          5 // Max 5 at a time
        );
        
        if (wellsToTransfer > 0) {
          const projectedFromScore = Math.max(0, from.workload.workloadScore - (wellsToTransfer * 2));
          const projectedToScore = Math.min(100, to.workload.workloadScore + (wellsToTransfer * 2));
          
          suggestions.push({
            from: { id: from.id, name: from.name, currentScore: from.workload.workloadScore },
            to: { id: to.id, name: to.name, currentScore: to.workload.workloadScore },
            wellsToTransfer,
            projectedFromScore,
            projectedToScore,
            impact: `Reduces ${from.name}'s load from ${from.workload.workloadScore} to ~${projectedFromScore}`,
            priority: from.workload.workloadStatus === 'overloaded' ? 'critical' : 'recommended',
          });
        }
      }
    }
    
    // Sort by priority and impact
    return suggestions.sort((a, b) => {
      if (a.priority === 'critical' && b.priority !== 'critical') return -1;
      if (b.priority === 'critical' && a.priority !== 'critical') return 1;
      return b.wellsToTransfer - a.wellsToTransfer;
    });
  }, [enrichedTeam]);
  
  // ─────────────────────────────────────────────────────────────────────────────
  // SELECTORS
  // ─────────────────────────────────────────────────────────────────────────────
  
  const getPumperWorkload = useCallback((targetPumperId: string): PumperWithWorkload | undefined => {
    return enrichedTeam.find(p => p.id === targetPumperId);
  }, [enrichedTeam]);
  
  const getPumpersByStatus = useCallback((status: WorkloadStatus): PumperWithWorkload[] => {
    return enrichedTeam.filter(p => p.workload.workloadStatus === status);
  }, [enrichedTeam]);
  
  const getRankedPumpers = useCallback((order: 'lightest' | 'heaviest'): PumperWithWorkload[] => {
    const sorted = [...enrichedTeam].sort((a, b) => 
      a.workload.workloadScore - b.workload.workloadScore
    );
    return order === 'lightest' ? sorted : sorted.reverse();
  }, [enrichedTeam]);
  
  // ─────────────────────────────────────────────────────────────────────────────
  // UTILITIES
  // ─────────────────────────────────────────────────────────────────────────────
  
  const formatScore = useCallback((score: number): string => {
    return `${Math.round(score)}/100`;
  }, []);
  
  const getStatusColor = useCallback((status: WorkloadStatus) => {
    return WORKLOAD_STATUS_COLORS[status];
  }, []);
  
  const getThresholds = useCallback(() => WORKLOAD_THRESHOLDS, []);
  
  const getWeights = useCallback(() => WORKLOAD_SCORE_WEIGHTS, []);
  
  // ─────────────────────────────────────────────────────────────────────────────
  // RETURN
  // ─────────────────────────────────────────────────────────────────────────────
  
  return {
    // State
    workload,
    breakdown,
    recommendations,
    teamComparison,
    teamSummary,
    loading,
    error,
    lastRefresh,
    
    // Actions
    refresh,
    calculateWorkload,
    getStatus,
    getRecommendations: getRecommendationsAction,
    quickCheck,
    refreshTeam,
    findBestPumperForAssignment,
    findPumpersNeedingRelief,
    suggestRebalancing,
    
    // Selectors
    getPumperWorkload,
    getPumpersByStatus,
    getRankedPumpers,
    
    // Utilities
    formatScore,
    getStatusColor,
    getThresholds,
    getWeights,
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// CONVENIENCE HOOKS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Hook for quick workload badge display
 * Use in pumper lists and dropdowns
 */
export function useWorkloadBadge(pumperId: string, user: { id: string; role: string } | null) {
  const [badge, setBadge] = useState<{
    status: WorkloadStatus;
    score: number;
    emoji: string;
    label: string;
    color: string;
    canAssign: boolean;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    if (!pumperId || !user) {
      setLoading(false);
      return;
    }
    
    quickWorkloadCheck(pumperId)
      .then(result => {
        const emoji = {
          light: '🟢',
          balanced: '🟡',
          heavy: '🟠',
          overloaded: '🔴',
        }[result.status];
        
        setBadge({
          status: result.status,
          score: result.score,
          emoji,
          label: WORKLOAD_STATUS_LABELS[result.status],
          color: WORKLOAD_STATUS_COLORS[result.status].text,
          canAssign: result.canAssign,
        });
      })
      .catch(() => {
        // Silent fail for badge
      })
      .finally(() => setLoading(false));
  }, [pumperId, user]);
  
  return { badge, loading };
}

/**
 * Hook for assignment dialog workload check
 * Shows warning if pumper is at capacity
 */
export function useAssignmentWorkloadCheck(
  pumperId: string | null,
  user: { id: string; role: string } | null
) {
  const [check, setCheck] = useState<{
    canAssign: boolean;
    status: WorkloadStatus;
    score: number;
    warning: string | null;
    suggestion: string;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  
  useEffect(() => {
    if (!pumperId || !user) {
      setCheck(null);
      return;
    }
    
    setLoading(true);
    quickWorkloadCheck(pumperId)
      .then(result => {
        const recommendation = WORKLOAD_STATUS_RECOMMENDATIONS[result.status];
        setCheck({
          ...result,
          suggestion: recommendation.suggestion,
        });
      })
      .catch(() => {
        setCheck(null);
      })
      .finally(() => setLoading(false));
  }, [pumperId, user]);
  
  return { check, loading };
}

/**
 * Hook for team dashboard summary
 * Optimized for dashboard display
 */
export function useTeamWorkloadDashboard(user: { id: string; role: string } | null) {
  const {
    teamSummary,
    teamComparison,
    loading,
    error,
    refreshTeam,
    findPumpersNeedingRelief,
    suggestRebalancing,
  } = usePumperWorkload({ user, includeTeamComparison: true });
  
  const atRiskCount = useMemo(() => 
    teamComparison.filter(c => 
      c.workloadStatus === 'overloaded' || c.workloadStatus === 'heavy'
    ).length,
    [teamComparison]
  );
  
  const healthGrade = useMemo(() => {
    if (!teamSummary) return 'N/A';
    const score = teamSummary.healthScore;
    if (score >= 90) return 'A';
    if (score >= 80) return 'B';
    if (score >= 70) return 'C';
    if (score >= 60) return 'D';
    return 'F';
  }, [teamSummary]);
  
  // Memoize function results to prevent re-computation on every render
  const pumpersNeedingRelief = useMemo(() => findPumpersNeedingRelief(), [findPumpersNeedingRelief]);
  const rebalancingSuggestions = useMemo(() => suggestRebalancing(), [suggestRebalancing]);
  
  return {
    summary: teamSummary,
    comparison: teamComparison,
    loading,
    error,
    refresh: refreshTeam,
    atRiskCount,
    healthGrade,
    pumpersNeedingRelief,
    rebalancingSuggestions,
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// EXPORT CONSTANTS FOR UI
// ═══════════════════════════════════════════════════════════════════════════════

export {
  WORKLOAD_THRESHOLDS,
  WORKLOAD_STATUS_LABELS,
  WORKLOAD_STATUS_EMOJI_LABELS,
  WORKLOAD_STATUS_DESCRIPTIONS,
  WORKLOAD_STATUS_COLORS,
  WORKLOAD_STATUS_RECOMMENDATIONS,
  WORKLOAD_SCORE_WEIGHTS,
  getWorkloadStatusFromScore,
  getWorkloadScoreBreakdown,
  createEmptyWorkload,
};

export default usePumperWorkload;
